/**
 * "First name + first initial of last name" (e.g. "Bella Sonia" → "Bella S"). Single-word names
 * pass through unchanged. Ports the mobile app's `String.shortName` Flutter extension so web and
 * mobile agree on how member names are shortened — including its "(Deleted User)" handling: the
 * suffix is stripped, the real name is shortened, and the suffix is re-appended.
 */

const DELETED_USER_SUFFIX = '(Deleted User)';

function shorten(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return parts[0] ?? '';
  const last = parts.at(-1) ?? '';
  return `${parts[0]} ${last[0]?.toUpperCase() ?? ''}`;
}

export function toShortName(fullName: string): string {
  const trimmed = fullName.trim();
  if (!trimmed) return '';

  if (trimmed.endsWith(DELETED_USER_SUFFIX)) {
    const actualName = trimmed.slice(0, trimmed.length - DELETED_USER_SUFFIX.length).trim();
    const shortened = shorten(actualName);
    return shortened ? `${shortened} ${DELETED_USER_SUFFIX}` : DELETED_USER_SUFFIX;
  }

  return shorten(trimmed);
}

/**
 * Shorten each name in a comma-separated list (e.g. "Lina, Bella Sonia, Shakshi Om" →
 * "Lina, Bella S, Shakshi O") — activity sentences carry multi-member placeholders this way.
 */
export function toShortNameList(names: string): string {
  if (!names.includes(',')) return toShortName(names);
  return names
    .split(',')
    .map((n) => toShortName(n))
    .filter(Boolean)
    .join(', ');
}
